import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Menu,
  X,
  ArrowRight,
  Check,
  Sparkles,
  Calendar,
  Truck,
  Hand,
  Wallet,
  Mail,
  Instagram,
  MapPin,
  Camera,
  Gift,
  Palette,
  Sun,
  Moon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTheme } from "@/context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_LINKS = [
  { id: "services", label: "Services" },
  { id: "how-it-works", label: "How it Works" },
  { id: "pricing", label: "Pricing" },
  { id: "gallery", label: "Gallery" },
  { id: "testimonials", label: "Why us" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function ThemeToggle({ overHero }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  // Over the dark hero image (top of page, not scrolled) — always show light icon.
  // Once scrolled, follow theme.
  const baseCls = overHero
    ? "text-white/70 hover:text-white"
    : "text-ink/60 hover:text-ink";
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className={`p-2 transition-colors duration-300 ${baseCls}`}
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Over the dark hero image: text always light. Once scrolled: theme-aware.
  const logoTextCls = scrolled ? "text-ink" : "text-white";
  const linkCls = scrolled
    ? "text-ink/70 hover:text-ink"
    : "text-white/70 hover:text-white";
  const ctaCls = scrolled
    ? "bg-ink text-surface hover:bg-gold hover:text-surface"
    : "bg-white text-obsidian hover:bg-gold hover:text-obsidian";
  const mobileToggleCls = scrolled ? "text-ink" : "text-white";

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-surface/80 backdrop-blur-xl border-b border-ink/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2 group"
        >
          <span
            className={`font-display text-3xl tracking-tight transition-colors duration-300 ${logoTextCls}`}
          >
            Givance
          </span>
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-gold" />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className={`link-reveal text-sm tracking-wide transition-colors duration-300 ${linkCls}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <ThemeToggle overHero={!scrolled} />
          <button
            data-testid="nav-cta-book"
            onClick={() => scrollTo("booking")}
            className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all duration-300 ${ctaCls}`}
          >
            Book a Kiosk
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle overHero={!scrolled} />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={mobileToggleCls}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-surface/95 backdrop-blur-xl border-t border-ink/10"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-base text-ink/80 py-2"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-cta-book"
              onClick={() => scrollTo("booking")}
              className="mt-4 bg-ink text-surface px-6 py-3 text-sm font-medium"
            >
              Book a Kiosk
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center"
    >
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1759519238029-689e99c6d19e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB3ZWRkaW5nJTIwcmVjZXB0aW9uJTIwYmFsbHJvb218ZW58MHx8fHwxNzc3MjM0MDU2fDA&ixlib=rb-4.1.0&q=85"
          alt="Luxury ballroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p
            data-testid="hero-overline"
            className="text-xs tracking-[0.3em] uppercase text-gold mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" />
            Givance · The modern way to gift
          </p>
          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[0.95] text-white"
          >
            A refined gifting experience for{" "}
            <span className="italic text-gold-grad">unforgettable</span> events.
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed font-light"
          >
            Simple. Secure. Made for your event. Givance brings an elegant
            touchscreen kiosk to your celebration — letting guests gift, give,
            and leave a message in seconds.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollTo("booking")}
              className="group inline-flex items-center justify-center gap-3 bg-white text-obsidian px-8 py-4 text-sm tracking-wide font-medium hover:bg-gold transition-all duration-500"
            >
              Book Your Kiosk
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-experiences"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-sm tracking-wide hover:bg-white/5 hover:border-white/60 transition-all duration-500"
            >
              View Experiences
            </button>
          </div>

          <div className="mt-20 grid grid-cols-3 gap-8 max-w-xl border-t border-white/10 pt-8">
            {[
              { v: "Cashless", l: "Tap to gift" },
              { v: "Bespoke", l: "Styled to your event" },
              { v: "Secure", l: "Card payments" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l}`}>
                <div className="font-display text-3xl text-white">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-2">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-xs tracking-[0.3em] uppercase animate-pulse">
        Scroll
      </div>
    </section>
  );
}

function SectionHeading({ overline, title, subtitle, align = "left" }) {
  return (
    <div
      className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}
    >
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4 flex items-center gap-3">
        {align === "center" ? null : <span className="h-px w-10 bg-gold" />}
        {overline}
      </p>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-ink">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-6 text-lg text-ink/60 leading-relaxed font-light">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-surface"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="The Setup"
            title="A complete cashless gifting experience."
            subtitle={'An elegant touchscreen kiosk with secure tap-to-pay, themed decoration to match your colour palette, and white-glove on-site setup. Some packages add a gift shop and a photobooth.'}
          />
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Big card */}
          <motion.div
            {...fadeUp}
            data-testid="service-card-flagship"
            className="md:col-span-7 group relative overflow-hidden bg-gradient-to-br from-panel-elevated via-panel-mid to-panel-soft border border-ink/10 hover:border-gold/40 transition-all duration-700 p-10 md:p-14 flex flex-col justify-between min-h-[520px]"
          >
            {/* Decorative gold corner accent */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gold/10 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-gold/5 blur-3xl rounded-full pointer-events-none" />

            <div className="relative">
              <p className="text-xs tracking-[0.3em] uppercase text-gold mb-6">
                The Givance Kiosk
              </p>
              <h3 className="font-display text-4xl md:text-5xl text-ink leading-tight mb-6">
                A touchscreen,{" "}
                <span className="italic text-gold-grad">styled</span> for your
                event.
              </h3>
              <p className="text-ink/60 leading-relaxed text-base md:text-lg max-w-xl">
                An elegant touchscreen kiosk dressed in your colour palette,
                branded with your event details, and ready the moment your
                guests arrive. They browse, choose an amount, leave a message,
                and tap to give.
              </p>
            </div>

            <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-ink/70 relative">
              {[
                "Crystal-clear touchscreen",
                "Secure tap-to-pay",
                "Themed branded interface",
                "All-day operation",
                "Custom welcome screens",
                "Cashless & paperless",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Small cards */}
          <motion.div
            {...fadeUp}
            data-testid="service-card-payouts"
            className="md:col-span-5 bg-panel/60 border border-ink/10 hover:border-gold/40 transition-all duration-700 p-10"
          >
            <Wallet className="w-8 h-8 text-gold mb-6" strokeWidth={1.2} />
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
              Transparent
            </p>
            <h3 className="font-display text-2xl text-ink mb-3">
              Clear gift reporting, every time
            </h3>
            <p className="text-ink/60 text-sm leading-relaxed">
              Card payments settle to your Stripe account. After the event you
              receive a transparent breakdown of every gift collected — Stripe
              releases payouts once their standard verification clears.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            data-testid="service-card-giftshop"
            className="md:col-span-5 bg-panel/60 border border-ink/10 hover:border-gold/40 transition-all duration-700 p-10"
          >
            <Sparkles className="w-8 h-8 text-gold mb-6" strokeWidth={1.2} />
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
              Gift Shop
            </p>
            <h3 className="font-display text-2xl text-ink mb-3">
              Default gifts — or your own selection
            </h3>
            <p className="text-ink/60 text-sm leading-relaxed">
              Default offering: flowers, chocolates, candles & drinks. Want
              something specific? We'll arrange a custom selection during the
              booking stage. Available on Celebration & Premium.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            data-testid="service-card-themed"
            className="md:col-span-7 bg-gradient-to-br from-panel-elevated to-panel-mid border border-ink/10 hover:border-gold/40 transition-all duration-700 p-10 flex flex-col justify-center"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
              Styled to your event
            </p>
            <h3 className="font-display text-3xl text-ink mb-3">
              Decorated to match your theme.
            </h3>
            <p className="text-ink/60 leading-relaxed max-w-xl">
              Balloons, styled backdrops, custom welcome screens, on-screen
              animations — every kiosk is dressed to feel like part of your
              event, not bolted on. Premium adds a built-in photobooth for a
              true centrepiece moment.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Reserve your date",
      icon: Calendar,
      text: "Tell us the event, date and venue. We confirm availability within 24 hours.",
    },
    {
      n: "02",
      title: "White-glove delivery",
      icon: Truck,
      text: "We arrive, set up, brand the screens to your event, and run a test transaction.",
    },
    {
      n: "03",
      title: "Guests tap & gift",
      icon: Hand,
      text: "Guests choose an amount, leave a message, and tap their card or phone. Effortless.",
    },
    {
      n: "04",
      title: "Payouts & breakdown",
      icon: Wallet,
      text: "After the event you receive a clear, transparent breakdown of every gift collected. Funds are paid out by Stripe to your chosen bank account once their standard verification clears.",
    },
  ];
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-36 border-t border-ink/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="The Experience"
            title="Four steps. Zero friction."
          />
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                data-testid={`step-${s.n}`}
                className="bg-surface p-10 group hover:bg-panel-mid transition-colors duration-500"
              >
                <div className="font-display text-6xl text-gold/30 group-hover:text-gold transition-colors duration-500">
                  {s.n}
                </div>
                <Icon
                  className="w-7 h-7 text-ink mt-8"
                  strokeWidth={1.2}
                />
                <h3 className="font-display text-2xl text-ink mt-4">
                  {s.title}
                </h3>
                <p className="text-ink/55 text-sm mt-3 leading-relaxed">
                  {s.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const tiers = [
    {
      name: "Essential",
      price: "£150",
      period: "",
      blurb: "For smaller or budget-conscious events.",
      features: [
        "Cash gifting (card payments)",
        "Guest name & message",
        "Basic screen customisation (event name & colours)",
        "Setup & collection",
      ],
      excluded: ["Gift shop", "Decorations", "Photobooth"],
      cta: "Book Essential",
      highlight: false,
      testid: "pricing-essential",
    },
    {
      name: "Celebration",
      price: "£200",
      period: "",
      blurb: "Where your real value shines — our most popular package.",
      features: [
        "Everything in Essential",
        "Gift shop (flowers, chocolates, candles, drinks)",
        "Full screen customisation (backgrounds, themes, images)",
        "Decorative setup (balloons + styled backdrop)",
        "Enhanced guest experience",
      ],
      excluded: [],
      cta: "Book Celebration",
      highlight: true,
      testid: "pricing-celebration",
    },
    {
      name: "Premium",
      price: "£250",
      period: "",
      blurb: "A centrepiece attraction at your event.",
      features: [
        "Everything in Celebration",
        "Photobooth feature",
        "Premium styled setup (enhanced decorations & layout)",
        "Fully immersive branded experience",
        "Custom welcome screens & animations",
        "Priority support",
      ],
      excluded: [],
      cta: "Book Premium",
      highlight: false,
      testid: "pricing-premium",
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-36 border-t border-ink/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="Investment"
            title="Transparent pricing. All-inclusive."
            subtitle="Every package includes white-glove delivery, custom branding, and dedicated event-day support. No hidden fees."
          />
        </motion.div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {tiers.map((t) => (
            <motion.div
              {...fadeUp}
              key={t.name}
              data-testid={t.testid}
              className={`relative p-10 border transition-all duration-700 hover:-translate-y-1 ${
                t.highlight
                  ? "bg-gradient-to-b from-panel-elevated to-panel-mid border-gold/60"
                  : "bg-panel/60 border-ink/10 hover:border-ink/30"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-10 px-3 py-1 bg-gold text-surface text-[10px] tracking-[0.25em] uppercase font-medium">
                  Most chosen
                </div>
              )}
              <h3 className="font-display text-3xl text-ink">{t.name}</h3>
              <p className="text-ink/55 mt-2 text-sm">{t.blurb}</p>
              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-5xl text-ink">
                  {t.price}
                </span>
                <span className="text-ink/50 text-sm">{t.period}</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-ink/70">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        t.highlight ? "text-gold" : "text-ink/60"
                      }`}
                    />
                    {f}
                  </li>
                ))}
                {t.excluded && t.excluded.length > 0 && (
                  <>
                    <li className="pt-3 mt-3 border-t border-ink/10 text-[10px] uppercase tracking-[0.25em] text-ink/40">
                      Not included
                    </li>
                    {t.excluded.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-3 text-ink/35 line-through"
                      >
                        <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-ink/30 no-underline" />
                        {f}
                      </li>
                    ))}
                  </>
                )}
              </ul>
              <button
                data-testid={`${t.testid}-cta`}
                onClick={() =>
                  document
                    .getElementById("booking")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className={`mt-10 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-sm tracking-wide transition-all duration-500 ${
                  t.highlight
                    ? "bg-gold text-surface hover:bg-ink"
                    : "border border-ink/20 text-ink hover:bg-ink/5"
                }`}
              >
                {t.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-ink/40 tracking-wider uppercase">
          All packages include setup & collection. Custom requirements? Get in touch.
        </p>
      </div>
    </section>
  );
}

function Gallery() {
  const images = [
    {
      src: "https://customer-assets.emergentagent.com/job_gifts-access/artifacts/1gc22ei4_IMG-20260316-WA0004%281%29.jpg",
      alt: "Givance kiosk styled at a 50th birthday celebration",
      caption: "Harriman's 50th Birthday",
      sub: "Black & gold styling · gifting & messages",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_gifts-access/artifacts/y27rfb4e_IMG-20260301-WA0005.jpg",
      alt: "Givance kiosk at Baby Amadi's gender reveal",
      caption: "Baby Amadi's Gender Reveal",
      sub: "Pink & ivory styling · custom welcome screen",
    },
  ];
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-36 border-t border-ink/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="In the Wild"
            title="Where elegance meets tap-to-give."
            subtitle="A glimpse from our recent celebrations — each one styled, branded and set up to feel part of the event."
          />
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {images.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`gallery-image-${i}`}
              className="relative overflow-hidden group aspect-[4/5] md:aspect-[5/6]"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-90 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                <p className="text-xs tracking-[0.25em] uppercase text-gold mb-2">
                  Featured event
                </p>
                <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight">
                  {img.caption}
                </h3>
                <p className="text-ink/60 text-sm mt-2">{img.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-ink/40 tracking-[0.2em] uppercase">
          More events to come — be one of our next stories.
        </p>
      </div>
    </section>
  );
}

function Testimonials() {
  const reasons = [
    {
      icon: Wallet,
      title: "Effortless cashless gifting",
      text: "Guests tap a card or phone in seconds. No envelopes, no awkward IOUs, no chasing — and every gift is instantly receipted to their email.",
    },
    {
      icon: Gift,
      title: "Built-in gift shop",
      text: "Beyond cash gifts, guests can purchase a default selection of flowers, chocolates, candles and drinks — delivered to the host. Custom requests can be arranged during booking.",
    },
    {
      icon: Palette,
      title: "Styled to your event",
      text: "We decorate the kiosk to match your colour palette and theme — balloons, backdrops, branded screens. It blends in beautifully.",
    },
    {
      icon: Camera,
      title: "Photobooth keepsake (Premium)",
      text: "Guests can snap a photo and leave a message. Photos are saved locally and sent to you after the event, alongside your full gift breakdown.",
    },
    {
      icon: Sparkles,
      title: "White-glove from start to finish",
      text: "We deliver, set up, run a test transaction, support throughout, and pack everything down. You enjoy your event.",
    },
    {
      icon: Wallet,
      title: "Transparent gift reporting",
      text: "After the event you receive a clear breakdown of every gift collected. Funds settle to your Stripe account once their standard verification clears — no surprises, no guesswork.",
    },
  ];
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-36 border-t border-ink/5 bg-panel-soft"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="Why hosts choose us"
            title="A new standard for celebratory giving."
            subtitle="A boutique service crafted for hosts who want their gifting moment to feel as considered as the rest of the event."
          />
        </motion.div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                {...fadeUp}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`reason-${i}`}
                className="relative bg-panel/60 backdrop-blur-md border border-ink/10 p-10 hover:border-gold/40 transition-all duration-500 group"
              >
                <Icon
                  className="w-8 h-8 text-gold mb-6 group-hover:scale-110 transition-transform duration-500"
                  strokeWidth={1.2}
                />
                <h3 className="font-display text-2xl text-ink leading-snug mb-3">
                  {r.title}
                </h3>
                <p className="text-ink/60 text-sm leading-relaxed">
                  {r.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    {
      q: "How does the kiosk actually work for guests?",
      a: "Guests simply tap the screen, choose an amount, then take a photo or leave a message for the host to see and keep.",
    },
    {
      q: "What's the gift shop option?",
      a: "On Celebration and Premium packages, guests can also purchase curated gifts directly from the kiosk — our default selection includes flowers, chocolates, candles and drinks, delivered to the host. If you'd like specific items beyond the defaults, we'll arrange that with you during the booking stage.",
    },
    {
      q: "Tell me more about the photobooth (Premium).",
      a: "Our Premium package adds a photobooth feature on the kiosk — guests can take a picture and leave a message. Photos are saved locally during the event and sent to you afterwards alongside your full gift breakdown — a beautiful, complete keepsake of the night.",
    },
    {
      q: "Is it secure?",
      a: "Yes. All payments are securely taken through Stripe and sent to the host.",
    },
    {
      q: "Can the kiosk be decorated to match my theme?",
      a: "Absolutely — that's a core part of what we do. We style the kiosk with balloons, backdrops and screen designs to match your colour palette and event theme. Celebration adds full screen customisation; Premium adds enhanced styled setup.",
    },
    {
      q: "What's the typical setup time on the day?",
      a: "We arrive 60–90 minutes before your event start time. Setup itself takes 15–30 minutes (a little longer for Premium with photobooth and full styling), and we run live test transactions before stepping back.",
    },
    {
      q: "When do we receive the funds?",
      a: "Stripe requires a short verification period before payouts can be released — this is standard across the industry to protect both hosts and guests. Funds are paid out as soon as Stripe completes verification, typically within a few business days. Either way, you receive a fully transparent breakdown after the event so you know exactly what's coming and when.",
    },
  ];
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-36 border-t border-ink/5"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <SectionHeading
            overline="Questions"
            title="Everything you need to know."
          />
        </motion.div>

        <Accordion
          type="single"
          collapsible
          className="mt-16 border-t border-ink/10"
          data-testid="faq-accordion"
        >
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-b border-ink/10"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="font-display text-xl text-ink hover:text-gold py-6 text-left"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="text-ink/60 text-base leading-relaxed pb-8"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_type: "",
    location: "",
    guest_count: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count || null,
        message: form.message || null,
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Enquiry received", {
        description:
          "Thank you. Our team will be in touch within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        event_type: "",
        location: "",
        guest_count: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Please check your details and try again.";
      toast.error("Could not submit", {
        description:
          typeof detail === "string" ? detail : "Please try again shortly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-ink/20 px-0 py-3 text-ink placeholder:text-ink/30 focus:outline-none focus:border-gold transition-colors text-base";
  const labelCls =
    "text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-2 block";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 md:py-36 border-t border-ink/5 bg-panel-soft"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Reserve
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-ink">
            Book your{" "}
            <span className="italic text-gold-grad">Givance</span>.
          </h2>
          <p className="mt-6 text-lg text-ink/60 leading-relaxed font-light">
            Share your event details and our team will respond within 24 hours
            with availability and a tailored quote.
          </p>

          <div className="mt-12 space-y-6 text-sm text-ink/70">
            <a
              href="mailto:GivanceUK@gmail.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 group hover:text-ink transition-colors"
            >
              <Mail className="w-4 h-4 text-gold flex-shrink-0" />
              <span data-testid="contact-email">GivanceUK@gmail.com</span>
            </a>
            <a
              href="https://instagram.com/givanceuk"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-instagram-link"
              className="flex items-center gap-4 group hover:text-ink transition-colors"
            >
              <Instagram className="w-4 h-4 text-gold flex-shrink-0" />
              <span data-testid="contact-instagram">@givanceuk</span>
            </a>
            <div className="flex items-center gap-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span data-testid="contact-location">
                UK-based · serving events nationwide
              </span>
            </div>
          </div>

          <div className="mt-16 p-6 border border-ink/10">
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
              White-glove promise
            </p>
            <p className="text-ink/60 text-sm leading-relaxed">
              Every booking includes delivery, setup, on-site support and
              breakdown. You focus on your guests — we handle everything else.
            </p>
          </div>
        </motion.div>

        <motion.form
          {...fadeUp}
          data-testid="booking-form"
          onSubmit={submit}
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"
        >
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-name">
              Full name *
            </label>
            <input
              id="bf-name"
              data-testid="booking-name"
              required
              value={form.name}
              onChange={update("name")}
              className={inputCls}
              placeholder="Your name"
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-email">
              Email *
            </label>
            <input
              id="bf-email"
              data-testid="booking-email"
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              className={inputCls}
              placeholder="you@example.com"
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-phone">
              Phone *
            </label>
            <input
              id="bf-phone"
              data-testid="booking-phone"
              required
              value={form.phone}
              onChange={update("phone")}
              className={inputCls}
              placeholder="+44 7000 000000"
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-date">
              Event date *
            </label>
            <input
              id="bf-date"
              data-testid="booking-date"
              required
              type="date"
              value={form.event_date}
              onChange={update("event_date")}
              className={`${inputCls} [color-scheme:dark]`}
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-type">
              Event type *
            </label>
            <select
              id="bf-type"
              data-testid="booking-type"
              required
              value={form.event_type}
              onChange={update("event_type")}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              <option value="" className="bg-surface text-ink">
                Select...
              </option>
              <option className="bg-surface text-ink">Wedding</option>
              <option className="bg-surface text-ink">Birthday</option>
              <option className="bg-surface text-ink">Baby Shower</option>
              <option className="bg-surface text-ink">Charity Gala</option>
              <option className="bg-surface text-ink">Corporate Event</option>
              <option className="bg-surface text-ink">Fundraiser</option>
              <option className="bg-surface text-ink">Other</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls} htmlFor="bf-guests">
              Estimated guests
            </label>
            <input
              id="bf-guests"
              data-testid="booking-guests"
              value={form.guest_count}
              onChange={update("guest_count")}
              className={inputCls}
              placeholder="e.g. 120"
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bf-location">
              Venue / Location *
            </label>
            <input
              id="bf-location"
              data-testid="booking-location"
              required
              value={form.location}
              onChange={update("location")}
              className={inputCls}
              placeholder="City, venue name"
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bf-message">
              Tell us about your event
            </label>
            <textarea
              id="bf-message"
              data-testid="booking-message"
              rows={4}
              value={form.message}
              onChange={update("message")}
              className={`${inputCls} resize-none`}
              placeholder="Theme, branding ideas, special requests..."
            />
          </div>
          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6">
            <p className="text-xs text-ink/40 tracking-wider">
              We respond within 24 hours, every time.
            </p>
            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-3 bg-ink text-surface px-10 py-4 text-sm tracking-wide font-medium hover:bg-gold transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send Enquiry"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      id="contact"
      data-testid="footer"
      className="relative bg-surface-deep border-t border-ink/5 pt-20 pb-12"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <span className="font-display text-4xl text-ink">Givance</span>
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-gold" />
            </div>
            <p className="mt-6 font-display italic text-2xl text-gold/80 max-w-md">
              The modern way to gift, give, and celebrate.
            </p>
            <p className="mt-6 text-ink/50 text-sm leading-relaxed max-w-md">
              A refined cashless gifting experience for weddings, birthdays,
              baby showers, gender reveals and corporate celebrations. Powered
              by Stripe.
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.3em] text-gold mb-6">
              Visit
            </h4>
            <ul className="space-y-3 text-sm text-ink/60">
              {NAV_LINKS.slice(0, 4).map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    className="hover:text-ink transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.3em] text-gold mb-6">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-ink/60">
              <li>
                <a
                  href="mailto:GivanceUK@gmail.com"
                  data-testid="footer-email"
                  className="hover:text-ink transition-colors"
                >
                  GivanceUK@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com/givanceuk"
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="footer-instagram"
                  className="inline-flex items-center gap-2 hover:text-ink transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5" />
                  @givanceuk
                </a>
              </li>
              <li>UK-based · nationwide</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-ink/10 pt-12">
          <div className="font-display italic text-[18vw] leading-none text-ink/[0.05] tracking-tighter text-center select-none">
            givance
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row md:justify-between gap-4 text-xs text-ink/40">
          <span>© {new Date().getFullYear()} Givance. All rights reserved.</span>
          <span>Powered by Stripe · PCI-compliant payments</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <main className="bg-surface text-ink font-body relative">
      <Nav />
      <Hero />
      <Services />
      <HowItWorks />
      <Pricing />
      <Gallery />
      <Testimonials />
      <FAQ />
      <BookingForm />
      <Footer />
    </main>
  );
}
